import { Input } from '@headlessui/react';
import { useState, ChangeEvent, FocusEvent, useEffect, useRef } from 'react';
import { FaEye, FaEyeSlash, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { IoInformationCircle } from 'react-icons/io5';
import { cn } from 'utils/cn';
import RequiredMark from 'components/common/RequiredMark';

interface IconOption {
  id: string;
  name: string;
  path: string;
}

interface FormInputProps {
  label?: string;
  name: string;
  type?: 'text' | 'password' | 'email' | 'number' | string;
  value: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  error?: string | false;
  size?: string;
  dis?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  darkBg?: boolean;
  showIconDropdown?: boolean;
  selectedIcon?: string;
  onIconChange?: (iconId: string) => void;
  iconOptions?: IconOption[];
  whiteLabel?: boolean;
  required?: boolean;
  info?: boolean;
  style?: React.CSSProperties;
  helperText?: string;
  className?: {
    label?: string;
    input?: string;
  };
}

const FormInput = ({
  label,
  name,
  type = 'text',
  value,
  onChange,
  onBlur,
  onFocus,
  error,
  size,
  dis,
  readOnly,
  placeholder,
  darkBg,
  showIconDropdown = false,
  selectedIcon = '',
  onIconChange,
  whiteLabel,
  required = false,
  info = false,
  helperText,
  className,
  iconOptions = [
    { id: 'pIcon1', name: 'Icon 1', path: '/assets/pIcon1.png' },
    { id: 'pIcon2', name: 'Icon 2', path: '/assets/pIcon2.png' },
    { id: 'pIcon3', name: 'Icon 3', path: '/assets/pIcon3.png' },
    { id: 'pIcon4', name: 'Icon 4', path: '/assets/pIcon4.png' },
    { id: 'pIcon5', name: 'Icon 5', path: '/assets/pIcon5.png' },
    { id: 'pIcon6', name: 'Icon 6', path: '/assets/pIcon6.png' },
  ],
}: FormInputProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isIconDropdownOpen, setIsIconDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleIconSelect = (iconId: string) => {
    if (onIconChange) {
      onIconChange(iconId);
    }
    setIsIconDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsIconDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const selectedIconData = iconOptions.find((icon) => icon.id === selectedIcon);

  // If showing icon dropdown, use horizontal layout
  if (showIconDropdown) {
    return (
      <div>
        {/* Horizontal Labels */}
        <div className="flex items-center  mb-[10px]">
          <div className="w-20">
            <span
              className={`${
                whiteLabel ? 'text-neutral font-medium' : 'text-neutral-medium'
              } text-sm`}
            >
              Icon
            </span>
          </div>
          <div className="flex-1">
            <span
              className={cn(
                `${
                  whiteLabel
                    ? 'text-neutral font-medium'
                    : 'text-neutral-medium'
                } text-sm`,
                className?.label,
              )}
            >
              {label}
              {''}
              {required && <RequiredMark />}
            </span>
          </div>
        </div>

        {/* Horizontal Input Layout */}
        <div className="flex items-start">
          {/* Icon Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setIsIconDropdownOpen(!isIconDropdownOpen)}
              disabled={dis}
              className={`flex items-center justify-center gap-2 h-[46px] px-3 border-primary-subtle rounded-lg ${
                darkBg ? 'bg-primary-light' : 'bg-primary-intemediate'
              } ${
                dis ? 'opacity-50 cursor-not-allowed' : 'hover:border-[#3B2770]'
              } transition-colors duration-200`}
              style={{
                borderColor: error ? 'red' : selectedIcon ? '#3B2770' : '',
              }}
            >
              {selectedIconData ? (
                <img
                  src={selectedIconData.path}
                  alt={selectedIconData.name}
                  className="w-6 h-6"
                />
              ) : (
                <div className="w-6 h-6 bg-gray-300 rounded"></div>
              )}
              {isIconDropdownOpen ? (
                <FaChevronUp className="w-3 h-3 text-neutral-medium transition-transform duration-200" />
              ) : (
                <FaChevronDown className="w-3 h-3 text-neutral-medium transition-transform duration-200" />
              )}
            </button>

            {/* Dropdown Menu with Animation */}
            <div
              className={`absolute top-full left-0 mt-1 border-[1px] border-primary-subtle rounded-lg shadow-xl z-10 p-4 w-48 
                bg-primary-variant
               transition-all duration-200 ease-in-out ${
                 isIconDropdownOpen
                   ? 'opacity-100 translate-y-0'
                   : 'opacity-0 -translate-y-2 pointer-events-none'
               }`}
            >
              <div className="grid grid-cols-3 gap-3">
                {iconOptions.map((icon) => (
                  <button
                    key={icon.id}
                    type="button"
                    onClick={() => handleIconSelect(icon.id)}
                    className={`p-2 rounded-lg transition-all duration-150 flex items-center justify-center ${
                      selectedIcon === icon.id
                        ? 'bg-primary-subtle ring-2 ring-[#3B2770]'
                        : 'hover:bg-primary-subtle hover:scale-105'
                    }`}
                  >
                    <img
                      src={icon.path}
                      alt={icon.name}
                      className="w-8 h-8 transition-transform duration-150"
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Input Field */}
          <div className="flex-1 relative">
            <Input
              type={type === 'password' && showPassword ? 'text' : type}
              name={name}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              readOnly={readOnly}
              disabled={dis}
              placeholder={placeholder}
              className={cn(
                `w-full  focus:ring-0 focus:outline-none border-[1px] px-4 border-primary-subtle h-[46px] ${size} placeholder:text-color-placeholder rounded-lg ${
                  darkBg ? 'bg-primary-light' : 'bg-primary-intemediate'
                } transition-colors duration-200`,
                className?.input,
              )}
              style={{
                borderColor: error ? 'red' : value ? '#3B2770' : '',
              }}
            />
            {error && <div className="text-red-500 text-xs mt-1">{error}</div>}
          </div>
        </div>
      </div>
    );
  }

  // Default layout for regular inputs
  return (
    <div>
      <div
        className={`flex justify-between items-center ${label || required || info ? 'mb-[11px]' : ''}`}
      >
        <label htmlFor={name} className={`flex items-center gap-2 w-full`}>
          <span
            className={cn(
              `block ${
                whiteLabel ? 'text-neutral font-medium' : 'text-[#9CA3AF]'
              } text-[14px] font-normal leading-[100%]`,
              className?.label,
            )}
          >
            {label}
            {required && <RequiredMark />}
          </span>
          {info && <IoInformationCircle className="text-neutral-medium" />}
        </label>
      </div>

      {/* <div className='relative'>
        <Input
          type={type === 'password' && showPassword ? 'text' : type}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          readOnly={readOnly}
          disabled={dis}
          placeholder={placeholder}
          className={`w-full focus:ring-0 focus:outline-none border-[1px] px-4 border-primary-subtle h-[46px] ${size} placeholder:text-color-placeholder rounded-lg ${
            darkBg ? 'bg-primary-light' : 'bg-primary-intemediate'
          } transition-colors duration-200`}
          style={{
            borderColor: error ? 'red' : value ? '#3B2770' : '',
          }}
        />
        {error && <div className='text-red-500 text-xs mt-1'>{error}</div>}
      </div> */}

      <div className="relative">
        <Input
          id={name}
          type={type === 'password' && showPassword ? 'text' : type}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          readOnly={readOnly}
          disabled={dis}
          placeholder={placeholder}
          className={cn(
            `w-full focus:ring-0 focus:outline-none border-[1px] px-4 ${
              type === 'password' ? 'pr-12' : ''
            } ${
              type === 'number'
                ? '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
                : ''
            } border-primary-subtle h-[46px] ${size} placeholder:font-normal placeholder:text-base placeholder:text-[#374151] rounded-[8px] ${
              darkBg ? 'bg-primary-light' : 'bg-primary-intemediate'
            } transition-colors duration-200`,
            className?.input,
          )}
          style={{
            borderColor: error ? 'red' : value ? '#3B2770' : '',
          }}
        />

        {type === 'password' && (
          <button
            type="button"
            tabIndex={-1}
            onClick={togglePasswordVisibility}
            className="absolute inset-y-0 right-3 flex items-center text-neutral-medium  transition-colors"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <FaEye size={16} /> : <FaEyeSlash size={16} />}
          </button>
        )}
      </div>
      {error && <div className="text-red-500 text-xs mt-1">{error}</div>}
      {helperText && !error && (
        <div className="text-gray-400 text-xs mt-1">{helperText}</div>
      )}
    </div>
  );
};

export default FormInput;
